import { useCallback, useEffect, useMemo, useReducer, useRef } from "react";
import { httpsCallable } from "firebase/functions";
import { firebaseFunctions } from "@lemonadetraining/web-shared";
import {
  calendarDateKey,
  type TodaysWorkoutRequest,
  type UserTodaysWorkoutResponse,
  type VisitCardTodaysWorkoutRequest,
} from "@lemonadetraining/contracts";
import { callableCode } from "../lib/callableError";
import { usableZone } from "../lib/gymTime";
import {
  groupTodayWorkouts,
  todayOffsetMinutes,
  type RawVisitCardToday,
  type TodayProgram,
} from "../lib/todayWorkouts";

export type TodaysWorkoutStatus = "idle" | "loading" | "ready" | "empty" | "error" | "clock-error";

// FirebaseError thrown by httpsCallable carries `code` (e.g.
// "functions/internal") alongside `message`.
export type TodaysWorkoutError = Error & { code?: string };

export interface UseTodaysWorkoutResult {
  status: TodaysWorkoutStatus;
  programs: TodayProgram[];
  /** One of the two sources failed: what is shown may not be everything. */
  partial: boolean;
  error: TodaysWorkoutError | null;
  refetch: () => Promise<void>;
}

interface UseTodaysWorkoutOptions {
  enabled?: boolean;
  /** Sent as `timeZone`, always; null uses the browser's zone. */
  gymZone?: string | null;
  /** The zone whose calendar date is "today" (`localDate`); null uses the gym's. */
  displayZone?: string | null;
}

interface FetchArgs {
  uid: string | null;
  gymZone: string | null;
  displayZone: string | null;
}

type Action =
  | { type: "idle" }
  | { type: "loading" }
  | { type: "done"; programs: TodayProgram[]; partial: boolean }
  | { type: "error"; error: TodaysWorkoutError }
  | { type: "clock-error"; error: TodaysWorkoutError };

const initialState: UseTodaysWorkoutResult = {
  status: "idle",
  programs: [],
  partial: false,
  error: null,
  refetch: async () => {},
};

function reducer(state: UseTodaysWorkoutResult, action: Action): UseTodaysWorkoutResult {
  switch (action.type) {
    case "idle":
      return state.status === "idle" ? state : { ...initialState, refetch: state.refetch };
    case "loading":
      return { ...state, status: "loading", error: null };
    case "done":
      return {
        ...state,
        status: action.programs.length > 0 ? "ready" : "empty",
        programs: action.programs,
        partial: action.partial,
        error: null,
      };
    case "error":
    case "clock-error":
      return { ...state, status: action.type, programs: [], partial: false, error: action.error };
  }
}

const asError = (err: unknown): TodaysWorkoutError =>
  err instanceof Error ? (err as TodaysWorkoutError) : new Error(String(err));

// The server refuses a localDate too far from its own clock ("localDate must be
// today's date"): retrying without one would quietly show the gym's day instead.
const isClockRefusal = (reason: unknown) =>
  callableCode(reason) === "invalid-argument" && /today's date/.test(asError(reason).message);

/**
 * Every workout for today: the member's programs (`userTodaysWorkout`) and
 * their visit card's (`getVisitCardTodaysWorkout`), grouped by program.
 * Pass `null` for `uid` to keep the hook idle. A response for an older
 * request is dropped, so the newest request wins.
 */
export function useTodaysWorkout(
  uid: string | null,
  options: UseTodaysWorkoutOptions = {}
): UseTodaysWorkoutResult {
  const { enabled = true, gymZone = null, displayZone = null } = options;
  const [state, dispatch] = useReducer(reducer, initialState);
  const argsRef = useRef<FetchArgs>({ uid, gymZone, displayZone });
  argsRef.current = { uid, gymZone, displayZone };
  const requestIdRef = useRef(0);

  const callables = useMemo(
    () => ({
      programs: httpsCallable<TodaysWorkoutRequest, UserTodaysWorkoutResponse>(
        firebaseFunctions,
        "userTodaysWorkout"
      ),
      visitCard: httpsCallable<VisitCardTodaysWorkoutRequest, RawVisitCardToday>(
        firebaseFunctions,
        "getVisitCardTodaysWorkout"
      ),
    }),
    []
  );

  const runFetch = useCallback(
    async ({ uid: userUID, gymZone: gym, displayZone: display }: FetchArgs, runId: number) => {
      if (!userUID) return;
      dispatch({ type: "loading" });
      // Read at call time so a refetch after midnight asks for the new day.
      const now = new Date();
      // The zone makes DST days exact; servers before it read the offset alone.
      const timeZone = usableZone(gym);
      const timezoneOffset = todayOffsetMinutes(now, timeZone);
      // The SDK sends undefined as null, which the server rejects: omit the key.
      const localDate = calendarDateKey(now, display ?? timeZone);
      const day = localDate === null ? {} : { localDate };
      const [programs, visitCard] = await Promise.allSettled([
        callables.programs({ userUID, timezoneOffset, timeZone, ...day }),
        callables.visitCard({ timezoneOffset, timeZone, ...day }),
      ]);
      if (runId !== requestIdRef.current) return;
      const refusal = [programs, visitCard].find(
        (r): r is PromiseRejectedResult => r.status === "rejected" && isClockRefusal(r.reason)
      );
      if (refusal) {
        dispatch({ type: "clock-error", error: asError(refusal.reason) });
        return;
      }
      if (programs.status === "rejected" && visitCard.status === "rejected") {
        dispatch({ type: "error", error: asError(programs.reason) });
        return;
      }
      const list =
        programs.status === "fulfilled" && Array.isArray(programs.value.data)
          ? programs.value.data
          : [];
      const card = visitCard.status === "fulfilled" ? (visitCard.value.data ?? null) : null;
      dispatch({
        type: "done",
        programs: groupTodayWorkouts(list, card),
        partial: programs.status === "rejected" || visitCard.status === "rejected",
      });
    },
    [callables]
  );

  useEffect(() => {
    requestIdRef.current += 1;
    if (!enabled || !uid) {
      dispatch({ type: "idle" });
      return;
    }
    void runFetch({ uid, gymZone, displayZone }, requestIdRef.current);
  }, [uid, enabled, gymZone, displayZone, runFetch]);

  const refetch = useCallback(async () => {
    if (!argsRef.current.uid) return;
    requestIdRef.current += 1;
    await runFetch(argsRef.current, requestIdRef.current);
  }, [runFetch]);

  return { ...state, refetch };
}
